import { useState, useEffect, useCallback } from 'react';
import { getAdminTransactions } from '../../api/adminApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const WALLET_LABELS = { main_wallet: 'Main', earning_wallet: 'Earning', one_wallet: 'One', reward_wallet: 'Reward' };

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const AdminTransactions = () => {
  const [txns, setTxns] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [txnType, setTxnType] = useState('');
  const [walletType, setWalletType] = useState('all');
  const [manualFilter, setManualFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    setLoading(true);
    getAdminTransactions({ page, limit: 30, txnType, walletType, manualFilter: manualFilter !== 'all' ? manualFilter : undefined, search })
      .then((res) => { setTxns(res.data.txns); setTotal(res.data.total); setPages(res.data.pages); })
      .finally(() => setLoading(false));
  }, [page, txnType, walletType, manualFilter, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Transactions</h4>
      <div style={{ color: '#555', fontSize: 13, marginBottom: 20 }}>{total} total records</div>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {/* Search */}
        <div>
          <input
            type="text"
            placeholder="Search by user code, email, or transaction ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: '100%',
              background: 'rgba(0,229,255,0.04)',
              border: '1px solid rgba(0,229,255,0.2)',
              borderRadius: 8,
              padding: '10px 14px',
              color: '#fff',
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        {/* Transaction Type, Wallet Type, & Manual Filter */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Transaction Type</label>
            <select
              value={txnType}
              onChange={(e) => { setTxnType(e.target.value); setPage(1); }}
              style={{
                width: '100%',
                background: '#1a1a1a',
                border: '1px solid rgba(0,229,255,0.2)',
                borderRadius: 8,
                padding: '8px 12px',
                color: '#fff',
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="" style={{ background: '#1a1a1a', color: '#fff' }}>All Types</option>
              <option value="credit" style={{ background: '#1a1a1a', color: '#66ff99' }}>Credit</option>
              <option value="debit" style={{ background: '#1a1a1a', color: '#ff6666' }}>Debit</option>
            </select>
          </div>

          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Wallet Type</label>
            <select
              value={walletType}
              onChange={(e) => { setWalletType(e.target.value); setPage(1); }}
              style={{
                width: '100%',
                background: '#1a1a1a',
                border: '1px solid rgba(0,229,255,0.2)',
                borderRadius: 8,
                padding: '8px 12px',
                color: '#fff',
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All Wallets</option>
              <option value="main_wallet" style={{ background: '#1a1a1a', color: '#00e5ff' }}>Main Wallet</option>
              <option value="earning_wallet" style={{ background: '#1a1a1a', color: '#66ff99' }}>Earning Wallet</option>
              <option value="one_wallet" style={{ background: '#1a1a1a', color: '#ffaa44' }}>One Wallet</option>
              <option value="reward_wallet" style={{ background: '#1a1a1a', color: '#bb86fc' }}>Reward Wallet</option>
            </select>
          </div>

          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Manual/Auto</label>
            <select
              value={manualFilter}
              onChange={(e) => { setManualFilter(e.target.value); setPage(1); }}
              style={{
                width: '100%',
                background: '#1a1a1a',
                border: '1px solid rgba(0,229,255,0.2)',
                borderRadius: 8,
                padding: '8px 12px',
                color: '#fff',
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all" style={{ background: '#1a1a1a', color: '#fff' }}>All Transactions</option>
              <option value="manual_credit" style={{ background: '#1a1a1a', color: '#66ff99' }}>Manual Credits</option>
              <option value="manual_debit" style={{ background: '#1a1a1a', color: '#ff6666' }}>Manual Debits</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflow: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '600px' : '100%' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)' }}>
              {['Txn ID', 'User', 'Wallet', 'Type', 'Amount', 'Comments', 'Manual?', 'Date'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td></tr>
            ) : txns.length === 0 ? (
              <tr><td colSpan={8} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No transactions.</td></tr>
            ) : txns.map((t) => {
              const user = t.user || t.walletId?.userId; // Support both aggregation and old format
              const walletType = t.wallet?.walletType || t.walletId?.walletType;
              return (
                <tr key={t._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ color: '#444', fontSize: 11, padding: '10px 14px', fontFamily: 'monospace' }}>{t._id.slice(-10).toUpperCase()}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ color: '#fff', fontSize: 12 }}>{user?.name || '—'}</div>
                    <div style={{ color: '#444', fontSize: 10, fontFamily: 'monospace' }}>{user?.userCode || user?.email}</div>
                  </td>
                  <td style={{ color: '#888', fontSize: 11, padding: '10px 14px' }}>{WALLET_LABELS[walletType] || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ background: t.txnType === 'credit' ? 'rgba(102,255,153,0.12)' : 'rgba(200,50,50,0.12)', color: t.txnType === 'credit' ? '#66ff99' : '#ff6666', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                      {t.txnType}
                    </span>
                  </td>
                  <td style={{ color: t.txnType === 'credit' ? '#66ff99' : '#ff6666', fontWeight: 700, fontSize: 13, padding: '10px 14px' }}>
                    {t.txnType === 'credit' ? '+' : '−'}{t.amount.toFixed(2)}
                  </td>
                  <td style={{ color: '#555', fontSize: 11, padding: '10px 14px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.comments || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    {t.isManual ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ background: 'rgba(255,170,68,0.12)', color: '#ffaa44', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600, display: 'inline-block', width: 'fit-content' }}>
                          Manual
                        </span>
                        {t['admin.name'] && (
                          <div style={{ color: '#888', fontSize: 10 }}>by {t['admin.name']}</div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#555', fontSize: 11 }}>Auto</span>
                    )}
                  </td>
                  <td style={{ color: '#444', fontSize: 11, padding: '10px 14px', whiteSpace: 'nowrap' }}>{fmtIST(t.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map((p) => (
            <button key={p} onClick={() => setPage(p)}
              style={{ background: p === page ? cyan : 'rgba(255,255,255,0.06)', border: 'none', color: p === page ? '#000' : '#888', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTransactions;
